import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { IonModal, IonToolbar, IonTitle, IonButtons, IonButton, IonContent, IonListHeader, IonItem, IonCheckbox, IonList, IonIcon, IonPopover, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonRow, IonCol, IonToast, IonHeader, getPlatforms, IonText, IonInput } from "@ionic/react";
import { trashOutline, downloadOutline, shareOutline, settingsOutline } from "ionicons/icons";
import React, { FC, useState, useEffect, SetStateAction, Dispatch, useMemo, useRef } from "react";
import { defaultMqttBroker, LoadPreference, PrefKey } from "components/AppSettings";
import { Glob, useLoc } from "functions/Language";
import { RichFormat, useMounted } from "functions/Util";
import { DropdownSelectItem } from "components/DropdownSelect";


export type Profile = {
	name: string,
	pass: string,
	broker: string,
}

const BROKER_NEW = -1;

export const ProfileDisplay: FC<{
	profile: Profile,
	otherProfiles: Profile[],
}> = (props) => {
	const need_broker = useMemo(() => {
		return props.otherProfiles.some(other =>
			other.name === props.profile.name
			&& other.broker !== props.profile.broker);
	}, [props.profile, props.otherProfiles]);

	return <>
		{props.profile.name}
		{need_broker && <>
			<br />
			<small>
				<IonText color="medium">{props.profile.broker}</IonText>
			</small>
		</>}
	</>
}

/** Sorts array in place and removes duplicate elements. */
function Arr_SortUnique<T>(arr: T[]) {
	arr.sort();

	for (let i = arr.length-1; i > 0; i--) { // 0 se nebere schvalne
		if (arr[i-1] === arr[i]) {
			arr.copyWithin(i-1, i, arr.length);
			arr.length--;
		}
	}

	return arr;
}

export function StringifyUsers(users : Profile[]) {
	return JSON.stringify(users.map(u => ({
		name: u.name,
		hash: btoa(u.pass),
		broker: u.broker,
	})));
}

export async function ParseUsers(json: string) : Promise<{ users: Profile[], err: false } | { err: true }> {
	let res: Profile[] = [];

	try {
		// TODO: lazy
		let old_broker = (await LoadPreference("mqtt-broker")) ?? defaultMqttBroker;

		let profs: Profile[] = [];
		let obj = JSON.parse(json);
		if (!Array.isArray(obj)) { // stary format
			profs = Object.keys(obj).map(name => ({
				name: name,
				pass: atob(obj[name]),
				broker: old_broker,
			}));

		}
		else {
			for (let user of obj) {
				profs.push({
					name: user["name"]?.toString() ?? "",
					pass: atob(user["hash"]?.toString() ?? ""),
					broker: user["broker"]?.toString() ?? old_broker,
				});
			}
		}

		// deduplikace
		for (let i = 0; i < profs.length; i++) {
			if (res.every(p => p.name !== profs[i].name || p.broker !== profs[i].broker)) {
				res.push(profs[i]);
			}
		}
	} catch {
		console.error("Failed to load profiles");
		return { err: true };
	}

	return { users: res, err: false };
}


export const ProfileSettings : FC<{
	selectedProfile: Profile,
	profiles: Profile[],
	updateProfiles: Dispatch<SetStateAction<Profile[]>>,
}> = (props) => {
	const loc = useLoc(glob);
	const [isOpen, setOpen] = useState(false);

	const [errorMessage, setErrorMessage] = useState<string|null>(null);
	const mounted = useMounted();

	const [select, setSelect] = useState(0);
	useEffect(function DeselectOnClose() { setSelect(0); }, [isOpen]);

	const userCount = props.profiles.length;
	const someSelected = select !== 0;
	const allSelected  = select === (1<<userCount)-1;

	const knownBrokers = useRef<string[]>([defaultMqttBroker]);
	useEffect(function GetKnownBrokers() {
		knownBrokers.current = Arr_SortUnique([defaultMqttBroker, ...props.profiles.map(u => u.broker)]);
	}, [props.profiles]);

	const [brokerId, setBrokerId] = useState(0);
	useEffect(function LoadBroker() {
		let id = knownBrokers.current.indexOf(props.selectedProfile.broker);
		if (id < 0) id = BROKER_NEW;
		setBrokerId(id);
	}, [props.selectedProfile, knownBrokers.current]);


	const [showForget, setShowForget] = useState(false);
	function DeleteSelected() {
		props.updateProfiles(old => {
			if (!select) return old;

			let copy: typeof old = [];

			for (let i = 0; i < old.length; i++) {
				if (!(select & (1 << i))) copy.push(old[i]);
			}

			return copy;
		});

		setSelect(0);
	}

	function ImportProfiles() {
		let input = document.createElement("input");
		input.setAttribute("type", "file");
		input.setAttribute("accept", "application/json");
		input.addEventListener("input", (e) => {
			// @ts-ignore
			let file = e.target.files[0];
			if (!file) {
				return;
			}

			let reader = new FileReader();
			reader.onload = async e => {
				if (!mounted.current) return;
				let profs = await ParseUsers(e.target?.result?.toString() ?? "{}");
				if (!mounted.current) return;

				if (profs.err) {
					setErrorMessage(loc.import_fail);
					return;
				}

				const copy = [...props.profiles];

				if (!copy[copy.length-1]?.name?.length) copy.pop();
				for (let i = 0; i < profs.users.length; i++) {
					let user = profs.users[i];
					if (!copy.some(p => p.name === user.name && p.broker === user.broker)) {
						copy.push(profs.users[i]);
					}
				}

				setErrorMessage(loc.import_succ);
				props.updateProfiles(copy);
			}
			reader.readAsText(file);
		});
		document.body.appendChild(input);
		input.click()
		document.body.removeChild(input);
	}

	async function ExportProfiles() {
		let json = StringifyUsers(props.profiles);

		if (getPlatforms().includes("desktop")) {
			// pc
			let element = document.createElement('a');
			element.setAttribute('href', 'data:text/json;charset=utf-8,' + encodeURIComponent(json));
			element.setAttribute('download', "pgm_users.json");
			element.style.display = 'none';
			document.body.appendChild(element);
			element.click();
			document.body.removeChild(element);

			setErrorMessage(loc.export_succ);
		} else {
			// iPhone by to mel umet
			// android dela neprijemnosti
			const path = "bmr_pgm_users.json";
			await Filesystem.writeFile({
				path: path,
				data: btoa(json),
				directory: Directory.Cache
			});
			const uri = await Filesystem.getUri({ path: path, directory: Directory.Cache });
			await Share.share({ title: path, url: uri.uri });

			if (mounted.current) {
				setErrorMessage(loc.export_succ);
			}
		}
	}



	return <>
	<IonButton onClick={() => setOpen(true)}>
		<IonIcon icon={settingsOutline} />
	</IonButton>

	<IonModal isOpen={isOpen} onWillDismiss={() => setOpen(false)} className="loginprofiles">
		<IonHeader>
			<IonToolbar color="tertiary">
				<IonTitle>{loc.title}</IonTitle>
				<IonButtons slot="end">
					<IonButton onClick={() => setOpen(false)}>{loc.close}</IonButton>
				</IonButtons>
			</IonToolbar>
		</IonHeader>

		<IonContent>
			{/* broker */}
			<IonListHeader>
				<RichFormat format={loc.sec_broker} arg={props.selectedProfile.name} />
			</IonListHeader>
			<div style={{ marginBottom: "1.5em" }}>
				<DropdownSelectItem
					label={undefined}
					options={knownBrokers.current}
					specialOptions={[{ key: BROKER_NEW, value: loc.brokerOther }]}
					selected={brokerId}
					setSelected={v => {
						setBrokerId(v);
						if (v !== BROKER_NEW) props.selectedProfile.broker = knownBrokers.current[v];
					}}
					lines="none"
				/>

				<IonInput mode="md"
					style={{ marginTop: "-.5em", display: brokerId === BROKER_NEW ? undefined : "none" }}
					className="inputlogin"
					type="text"
					fill="outline"
					required
					value={props.selectedProfile.broker}
					onIonInput={e => {
						props.selectedProfile.broker = e.detail.value ?? "";

						let id = knownBrokers.current.indexOf(props.selectedProfile.broker);
						if (id < 0) id = BROKER_NEW; // tohle se technicky zredukuje na `if (id === -1) id = -1`, ale to mi prijde krehke
						setBrokerId(id);
					}}
				/>
			</div>

			<hr />

			<IonListHeader>{loc.sec_delete}</IonListHeader>
			<IonItem>
				<IonCheckbox
					color="secondary"
					indeterminate={someSelected && !allSelected} checked={allSelected}
					onIonChange={ev => {
						if (ev.detail.checked) setSelect((1<<userCount)-1);
						else setSelect(0);
					}}
				>
					{loc.delete_all}
				</IonCheckbox>
			</IonItem>
			<IonList>
			{props.profiles.map((user, i) =>
				<IonItem key={i} lines="none">
					<IonCheckbox checked={!!(select & (1 << i))} onIonChange={ev => {
						setSelect(old => {
							const bit = 1 << i;
							if (ev.detail.checked) return old | bit;
							else return old &~ bit;
						});
					}}>
						<ProfileDisplay profile={user} otherProfiles={props.profiles} />
					</IonCheckbox>
				</IonItem>
			)}
			</IonList>

			<IonButton onClick={() => setShowForget(true)} color="danger" disabled={select === 0}>
				<IonIcon icon={trashOutline} />
				{loc.delete_selected}
			</IonButton>

			<hr />

			<IonListHeader>{loc.sec_backup}</IonListHeader>
			<div>
				<IonButton onClick={ExportProfiles}>
					<IonIcon icon={downloadOutline} />
					{loc.export}
				</IonButton>

				<IonButton onClick={ImportProfiles}>
					<IonIcon icon={shareOutline} />
					{loc.import}
				</IonButton>
			</div>
		</IonContent>

		<IonPopover isOpen={showForget} onDidDismiss={() => setShowForget(false)} className='popover'>
			<IonCard>
				<IonCardHeader>
					<IonCardTitle>{loc.forget}</IonCardTitle>
				</IonCardHeader>
				<IonCardContent>
					<IonRow>
						<IonCol>
							<IonButton expand='block' onClick={() => setShowForget(false)}>{loc.no}</IonButton>
						</IonCol>
						<IonCol>
						<IonButton color='danger' expand='block' onClick={() => {
							DeleteSelected();
							setShowForget(false);
						}}>{loc.yes}</IonButton>
						</IonCol>
					</IonRow>
				</IonCardContent>
			</IonCard>
		</IonPopover>

		<IonToast
			isOpen={errorMessage !== null}
			onDidDismiss={() => setErrorMessage(null)}
			message={errorMessage ?? ""}
			position="top"
			duration={5000}
			buttons={[ { text: 'OK', role: 'cancel', handler: () => setErrorMessage(null) } ]}
		/>
	</IonModal>
	</>
}

const glob = Glob({
	en: {
		title: "User management",
		close: "Close",

		sec_delete: "Deletion",
		delete_all: "Select all",
		delete_selected: "Delete selected",
		forget: "Do you wish to delete selected users?",
		yes: "Yes",
		no: "No",

		sec_broker: "MQTT broker for selected user ({0})",
		brokerOther: "Other...",

		sec_backup: "Import / export",
		export: "Backup",
		import: "Restore",

		import_succ: "Successfully loaded users.",
		import_fail: "Failed to load users.",
		export_succ: "Successfully saved users.",
	},
	cs: {
		title: "Správa uživatelů",
		close: "Zavřít",

		sec_broker: "MQTT broker pro vybraného uživatele ({0})",
		brokerOther: "Jiný...",

		sec_delete: "Mazání",
		delete_all: "Vybrat všechny",
		delete_selected: "Smazat vybrané",
		forget: "Chcete vybrané uživatele smazat?",
		yes: "Ano",
		no: "Ne",

		sec_backup: "Import / export",
		export: "Zálohovat",
		import: "Obnovit zálohu",

		import_succ: "Uživatelé byli načteni.",
		import_fail: "Uživatele nelze načíst.",
		export_succ: "Uživatelé byli uloženi.",
	},
	pg: {
		title: "Gestão de usuários",
		close: "Fechar",

		sec_delete: "Exclusão",
		delete_all: "Selecionar tudo",
		delete_selected: "Excluir usuários selecionados",
		forget: "Deseja excluir os usuários selecionados?",
		yes: "Sim",
		no: "Não",

		sec_broker: "Broker MQTT para o usuário selecionado ({0})",
		brokerOther: "Outro",

		sec_backup: "Import / export",
		export: "Fazer backup",
		import: "Restaurar backup",

		import_succ: "Carregamento dos usuários concluído com sucesso.",
		import_fail: "Falha ao carregar os usuários.",
		export_succ: "Salvamento dos usuários concluído com sucesso.",
	},
	es: {
		title: "Gestión de usuarios",
		close: "Cerrar",

		sec_broker: "Broker MQTT para el usuario seleccionado ({0})",
		brokerOther: "Otro",

		sec_delete: "Eliminación",
		delete_all: "Seleccionar todo",
		delete_selected: "Eliminar los usuarios seleccionados",
		forget: "¿Quieres eliminar a los usuarios seleccionados?",
		yes: "Sí",
		no: "No",

		sec_backup: "Import / export",
		export: "Exportar",
		import: "Importar",

		import_succ: "Usuarios cargados correctamente.",
		import_fail: "No se han podido cargar los usuarios.",
		export_succ: "Usuarios guardados correctamente.",
	},
} as const);

